import { ApiResponseError } from '../api/envelope';

/** What the API tells us when credit runs short. All amounts are Naira. */
export interface CreditShortfall {
  requiredNgn: number;
  balanceNgn: number;
  shortfallNgn: number;
}

/**
 * True when a request was refused for credit rather than failing.
 *
 * This is not an error to display — it's a prompt to top up. Callers branch on
 * it to open the top-up drawer instead of rendering a red alert.
 */
export function isInsufficientCredits(err: unknown): err is ApiResponseError {
  return err instanceof ApiResponseError && err.code === 'INSUFFICIENT_CREDITS';
}

/**
 * The numbers from a 402, or null if it isn't one. The API sends these precisely
 * so the UI can say how much short the user is instead of making them work it
 * out from a balance and a price.
 */
export function readShortfall(err: unknown): CreditShortfall | null {
  if (!isInsufficientCredits(err)) return null;
  const d = err.details ?? {};
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return {
    requiredNgn: num(d.requiredNgn),
    balanceNgn: num(d.balanceNgn),
    shortfallNgn: num(d.shortfallNgn),
  };
}

/**
 * Thrown in place of the original error once the top-up flow has taken over, so
 * the calling panel knows to render nothing: the user is looking at a drawer
 * about payment, and a second error message underneath it is just noise.
 *
 * Mirrors SignInRequiredError, which solves the same problem for the sign-in gate.
 */
export class CreditsRequiredError extends Error {
  constructor() {
    super('Top-up required');
    this.name = 'CreditsRequiredError';
  }
}

export function isCreditsRequired(err: unknown): err is CreditsRequiredError {
  return err instanceof CreditsRequiredError;
}

/** Naira, with thousands separators and kobo only when they matter. */
export function formatNgn(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  const whole = Number.isInteger(rounded);
  return `₦${rounded.toLocaleString('en-NG', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}
